import { Subject } from 'rxjs';

/**
 * @module Utils.JobQueue
 */

/*
 * @description A Queue for jobs that can control concurrency and throttling.
 *     "Jobs" must be methods that return a promises and take a single param object.
 * @param {Object} config configuration object for the jobQueue
 * @param {Window} config.window global browser window context
 * @param {number|undefined} config.concurrentJobs number of jobs that can happen at the same time,
 *     or falsy to allow any number of concurrent jobs
 * @param {number|undefined} config.jobsPerSec the throttling rate in jobs per second,
 *     or falsy to not throttle jobs
 * @param {number|undefined} config.maxSize maximum size of the que,
 *     or falsy to not limit queue size
 * @returns {JobQueue}
 */
function createJobQueue(config) {
    const _JobQueue = {};
    const waitingJobs = [];
    const runningJobs = [];
    const times = [];
    let interval;

    function matchesMethod(item) {
        return !this.method || item.method === this.method;
    }

    function process() {
        if (config.jobsPerSec) {
            const now = config.window.Date.now();

            // remove older times
            let i = 0;
            while (i < times.length) {
                if ((now - times[i]) > (10000 / config.jobsPerSec)) {
                    times.splice(i, 1);
                } else {
                    i += 1;
                }
            }

            // throttle the next job if too many happened recently
            const jobsWithinTheLastSecond = times.filter(t => now - t < 1000);
            if (jobsWithinTheLastSecond.length >= config.jobsPerSec) {
                // check later for waiting jobs that might be throttled by jobsPerSec
                if (waitingJobs.length > 0 && !interval) {
                    interval = config.window.setInterval(process, 1000 / config.jobsPerSec);
                }
                return;
            }
        }

        // run a job form the queue if concurrency rules allow
        if ((!config.concurrentJobs || runningJobs.length < config.concurrentJobs) && waitingJobs.length > 0) {
            let queItem = waitingJobs.shift();
            runningJobs.push(queItem);
            if (config.jobsPerSec) {
                times.push(config.window.Date.now());
            }
            queItem.method(queItem.params).then(
                result => {
                    runningJobs.splice(runningJobs.indexOf(queItem), 1);
                    queItem.subject.next(result);
                    queItem.subject.complete();
                    process();
                },
                e => {
                    runningJobs.splice(runningJobs.indexOf(queItem), 1);
                    queItem.subject.error(e);
                    process();
                }
            );
        }

        if (config.jobsPerSec) {
            // do not keep checking for more jobs if there are none
            if (waitingJobs.length === 0 && interval) {
                config.window.clearInterval(interval);
                interval = undefined;
            }
        }
    }

    function createQueItem(method, params, arrayMethod) {
        const subject = new Subject();
        if (!config.maxSize || waitingJobs.length + 1 < config.maxSize) {
            waitingJobs[arrayMethod]({
                method: method,
                params: params,
                subject: subject
            });
            process();
        } else {
            subject.error({
                code: 'JobQueue is full, max size is ' + config.maxSize,
                method: method,
                params: params
            });
        }
        return subject.asObservable();
    }

    /**
     * @description Adds a new job to the end of the queue
     * @param {Function} method A function to run when the queue is ready. The function must return a Promise.
     * @param {Object} params An object representing parameters to pass to the 'method' function
     * @returns {Observable} Emits when the job is run and its Promise resolves
     *     Clients can treat this as if it came directly from a non-queued observable.
     */
    _JobQueue.add = function(method, params) {
        return createQueItem(method, params, 'push');
    };

    /**
     * @description Adds a new job to the beginning of the queue, ahead of any existing items
     * @param {Function} method A function to run when the queue is ready. The function must return a Promise.
     * @param {Object} params An object representing parameters to pass to the 'method' function
     * @returns {Observable} Emits when the job is run and its Promise resolves
     *     Clients can treat this as if it came directly from a non-queued observable.
     */
    _JobQueue.addToFront = function(method, params) {
        return createQueItem(method, params, 'unshift');
    };

    /**
     * @param {function|undefined} method When provided, the queue will be filtered for only items
     *     that have this given method before calculating length.
     * @returns {Number} The total number of jobs that are either waiting or running.
     */
    _JobQueue.length = function(method) {
        return waitingJobs.filter(matchesMethod, { method: method }).length
            + runningJobs.filter(matchesMethod, { method: method }).length;
    };

    /**
     * @description Clears out all jobs both waiting and running,
     *     but will not stop currently running jobs.
     *     This should be called when the user becomes logged out the the session expires.
     */
    _JobQueue.clear = function() {
        waitingJobs.splice(0);
        runningJobs.splice(0);
        times.splice(0);
        if (interval) {
            config.window.clearInterval(interval);
        }
    };

    return Object.freeze(_JobQueue);
}

export default createJobQueue;
